/**
 * Image Path Helper - Utility functions for handling image paths
 * Converts between preview and thumbnail paths
 * Handles both relative and absolute URLs
 * 
 * Storage Strategy:
 * - Database stores only filename (e.g., "uuid_wm.jpg")
 * - Frontend constructs full paths based on context:
 *   - Cards: prompts/thumbnails/{filename}
 *   - Detail: prompts/previews/{filename}
 */

import { API_CONFIG } from '../config';

/**
 * Get full path for preview image from filename
 * 
 * @param filename - Filename only (e.g., "uuid_wm.jpg")
 * @returns Full relative path (e.g., "prompts/previews/uuid_wm.jpg")
 */
export function getPreviewPath(filename: string | undefined): string | undefined {
  if (!filename) return undefined;
  return `prompts/previews/${filename}`;
}

/**
 * Get full path for thumbnail image from filename
 * 
 * @param filename - Filename only (e.g., "uuid_wm.jpg")
 * @returns Full relative path (e.g., "prompts/thumbnails/uuid_wm.jpg")
 */
export function getThumbnailPath(filename: string | undefined): string | undefined {
  if (!filename) return undefined;
  return `prompts/thumbnails/${filename}`;
}

/**
 * Get full URL for an image path
 * Handles both relative paths and absolute URLs
 * 
 * @param imagePath - Relative or absolute image path
 * @returns Full URL to the image
 * 
 * @example
 * getImageUrl('prompts/previews/image_wm.jpg')
 * // Returns: 'http://api.example.com/storage/prompts/previews/image_wm.jpg'
 */
export function getImageUrl(imagePath: string | undefined): string | undefined {
  if (!imagePath) return undefined;

  // Already a full URL
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // Relative path - prepend API base and storage path
  return `${API_CONFIG.BASE}/storage/${imagePath}`;
}

/**
 * Get thumbnail URL from filename
 * Constructs thumbnail path and returns full URL
 * 
 * @param filename - Filename only (e.g., "uuid_wm.jpg")
 * @returns Full URL to thumbnail image
 * 
 * @example
 * getThumbnailUrl('image_wm.jpg')
 * // Returns: 'http://api.example.com/storage/prompts/thumbnails/image_wm.jpg'
 */
export function getThumbnailUrl(filename: string | undefined): string | undefined {
  const thumbnailPath = getThumbnailPath(filename);
  return getImageUrl(thumbnailPath);
}

/**
 * Get preview URL from filename
 * Constructs preview path and returns full URL
 * 
 * @param filename - Filename only (e.g., "uuid_wm.jpg")
 * @returns Full URL to preview image
 * 
 * @example
 * getPreviewUrl('image_wm.jpg')
 * // Returns: 'http://api.example.com/storage/prompts/previews/image_wm.jpg'
 */
export function getPreviewUrl(filename: string | undefined): string | undefined {
  const previewPath = getPreviewPath(filename);
  return getImageUrl(previewPath);
}

/**
 * Check if a value is a filename only (no path separators)
 * 
 * @param value - Value to check
 * @returns True if value is a filename only
 */
export function isFilenameOnly(value: string | undefined): boolean {
  if (!value) return false;
  return !value.includes('/');
}

/**
 * Extract filename from full path
 * 
 * @param imagePath - Full path or filename
 * @returns Filename only
 * 
 * @example
 * getFilename('prompts/previews/image_wm.jpg') // Returns: 'image_wm.jpg'
 * getFilename('image_wm.jpg') // Returns: 'image_wm.jpg'
 */
export function getFilename(imagePath: string | undefined): string | undefined {
  if (!imagePath) return undefined;
  return imagePath.split('/').pop();
}

export default {
  getPreviewPath,
  getThumbnailPath,
  getImageUrl,
  getThumbnailUrl,
  getPreviewUrl,
  isFilenameOnly,
  getFilename,
};
